import { getMessaging, getToken, onMessage, deleteToken, Messaging } from 'firebase/messaging';
import { app } from './core';
import { updateData } from './api-wrapper.service';
import { getCurrentUser } from './auth.service';
import logger from '../../utils/logger';

// Service worker registration file path
const SW_PATH = '/firebase-messaging-sw.js';

// Platform detection
const getPlatform = (): string => {
  const userAgent = navigator.userAgent.toLowerCase();
  
  if (/android/.test(userAgent)) {
    return 'android';
  } else if (/iphone|ipad|ipod/.test(userAgent)) {
    return 'ios';
  } else if (/macintosh|mac os x/.test(userAgent)) {
    return 'macos';
  } else if (/windows/.test(userAgent)) {
    return 'windows';
  } else if (/linux/.test(userAgent)) {
    return 'linux';
  }
  
  return 'web';
};

// Check if messaging is supported in current browser
const isMessagingSupported = (): boolean => {
  return 'Notification' in window && 
         'serviceWorker' in navigator && 
         'PushManager' in window;
};

let messaging: Messaging | null = null;

// Initialize messaging instance
const initializeMessaging = (): Messaging | null => {
  if (!isMessagingSupported()) {
    logger.warn('Push notifications are not supported in this browser');
    return null;
  }

  if (!messaging) {
    try {
      messaging = getMessaging(app);
    } catch (error) {
      logger.error('Error initializing Firebase Messaging:', error);
      return null;
    }
  }

  return messaging;
};

/**
 * Request notification permission from the user
 * @returns Promise<NotificationPermission>
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  try {
    const permission = await Notification.requestPermission();
    logger.info('Notification permission:', permission);
    return permission;
  } catch (error) {
    logger.error('Error requesting notification permission:', error);
    return 'denied';
  }
};

/**
 * Register service worker for push notifications
 * @returns Promise<ServiceWorkerRegistration | null>
 */
export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!('serviceWorker' in navigator)) {
    logger.warn('Service Worker is not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register(SW_PATH);
    logger.info('Service Worker registered:', registration);
    return registration;
  } catch (error) {
    logger.error('Service Worker registration failed:', error);
    return null;
  }
};

/**
 * Get FCM token for the current user
 * @returns Promise<string | null>
 */
export const getFCMToken = async (): Promise<string | null> => {
  const messagingInstance = initializeMessaging();
  if (!messagingInstance) {
    return null;
  }

  try {
    // Ensure we have notification permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      logger.warn('Notification permission not granted');
      return null;
    }

    // Register service worker if not already registered
    const registration = await registerServiceWorker();
    if (!registration) {
      logger.error('Service worker registration required for FCM');
      return null;
    }

    // Get the token
    const token = await getToken(messagingInstance, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration
    });

    if (token) {
      logger.info('FCM token obtained successfully');
      return token;
    } else {
      logger.warn('No FCM token available');
      return null;
    }
  } catch (error) {
    logger.error('Error getting FCM token:', error);
    return null;
  }
};

/**
 * Save push token to user profile
 * @param userId User ID
 * @param token FCM token
 * @returns Promise<void>
 */
export const savePushTokenToProfile = async (userId: string, token: string): Promise<void> => {
  try {
    const platform = getPlatform();
    const updates = {
      pushToken: token,
      platform,
      pushTokenUpdatedAt: Date.now()
    };

    await updateData(`users/${userId}`, updates);
    logger.info('Push token saved to user profile');
  } catch (error) {
    logger.error('Error saving push token to profile:', error);
    throw error;
  }
};

/**
 * Clear push token from user profile (used on logout)
 * @param userId User ID
 * @returns Promise<void>
 */
export const clearPushTokenFromProfile = async (userId: string): Promise<void> => {
  try {
    const updates = {
      pushToken: null,
      pushTokenUpdatedAt: Date.now()
    };

    await updateData(`users/${userId}`, updates);
    logger.info('Push token cleared from user profile');
  } catch (error) {
    logger.error('Error clearing push token from profile:', error);
    throw error;
  }
};

/**
 * Delete FCM token (unregister from push notifications)
 * @returns Promise<boolean>
 */
export const deleteFCMToken = async (): Promise<boolean> => {
  const messagingInstance = initializeMessaging();
  if (!messagingInstance) {
    return false;
  }

  try {
    await deleteToken(messagingInstance);
    logger.info('FCM token deleted successfully');
    return true;
  } catch (error) {
    logger.error('Error deleting FCM token:', error);
    return false;
  }
};

/**
 * Initialize push notifications for the current user
 * This should be called after successful login
 * @returns Promise<void>
 */
export const initializePushNotifications = async (): Promise<void> => {
  try {
    const user = getCurrentUser();
    if (!user) {
      logger.warn('No authenticated user, skipping push notification initialization');
      return;
    }

    // Check if browser supports push notifications
    if (!isMessagingSupported()) {
      logger.warn('Push notifications not supported in this environment');
      return;
    }

    // Get FCM token
    const token = await getFCMToken();
    if (!token) {
      logger.warn('Failed to get FCM token');
      return;
    }

    // Save token to user profile
    await savePushTokenToProfile(user.uid, token);

    // Set up message listener for foreground notifications
    setupMessageListener();

    logger.info('Push notifications initialized successfully');
  } catch (error) {
    logger.error('Error initializing push notifications:', error);
  }
};

/**
 * Clean up push notifications on logout
 * @returns Promise<void>
 */
export const cleanupPushNotifications = async (): Promise<void> => {
  try {
    const user = getCurrentUser();
    if (!user) {
      return;
    }

    // Clear token from profile
    await clearPushTokenFromProfile(user.uid);

    // Delete the FCM token
    await deleteFCMToken();

    logger.info('Push notifications cleaned up successfully');
  } catch (error) {
    logger.error('Error cleaning up push notifications:', error);
  }
};

/**
 * Set up listener for foreground messages
 */
const setupMessageListener = (): void => {
  const messagingInstance = initializeMessaging();
  if (!messagingInstance) {
    return;
  }

  onMessage(messagingInstance, (payload) => {
    logger.info('Received foreground message:', payload);

    // Check if we have notification data
    const notificationTitle = payload.notification?.title || 'New notification';
    const notificationOptions = {
      body: payload.notification?.body || '',
      icon: payload.notification?.icon || '/icon-192x192.png',
      badge: '/icon-72x72.png',
      tag: payload.data?.tag || 'default',
      data: payload.data,
      requireInteraction: false,
      silent: false
    };

    // Show notification if we have permission
    if (Notification.permission === 'granted') {
      const notification = new Notification(notificationTitle, notificationOptions);
      
      // Handle notification click
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        
        // Handle navigation based on notification data
        if (payload.data?.url) {
          window.location.href = payload.data.url;
        }
        
        notification.close();
      };
    }
  });
};

/**
 * Request to refresh FCM token (called periodically or on app update)
 * @returns Promise<void>
 */
export const refreshPushToken = async (): Promise<void> => {
  try {
    const user = getCurrentUser();
    if (!user) {
      return;
    }

    // Get new token
    const token = await getFCMToken();
    if (!token) {
      return;
    }

    // Update in profile
    await savePushTokenToProfile(user.uid, token);
    
    logger.info('Push token refreshed successfully');
  } catch (error) {
    logger.error('Error refreshing push token:', error);
  }
};

/**
 * Check if push notifications are enabled
 * @returns boolean
 */
export const arePushNotificationsEnabled = (): boolean => {
  return isMessagingSupported() && Notification.permission === 'granted';
};

/**
 * Get notification permission status
 * @returns NotificationPermission
 */
export const getNotificationPermissionStatus = (): NotificationPermission => {
  if (!('Notification' in window)) {
    return 'denied';
  }
  return Notification.permission;
};